import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import RouteLink from 'flow/components/ui-components/RouteLink';

import Loading from 'flow/components/ui-components/Loading';
import Page from 'flow/components/Setting/Page';
import Divider from 'flow/components/ui-components/Divider';
import SettingSection from 'flow/components/Setting/SettingSection';
import useAppContext from 'flow/hooks/useAppContext';
import { useAutoReplyTemplates } from 'flow/hooks/api/useAutoReplyTemplates';
import {
    selectOmniTicketConfiguredMessageTypes,
    selectQueueProfiles,
} from 'flow/selectors/queue-selectors';
import { AUTO_REPLY_MESSAGE_TYPES } from 'flow/types/AutoReplyTemplate';
import { QueueKey } from 'flow/types/EntityKeys';
import { PbxParams } from 'flow/types/Params';
import { routes } from 'flow/types/routes';
import { generatePath } from 'flow/utils/router-utils';

const PbxAutoReply = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { entityKey } = useParams<PbxParams>();
    const queueKey = entityKey as QueueKey;

    const profiles = useAppSelector((state) =>
        selectQueueProfiles(state, queueKey),
    );

    const configuredMessageTypes = useAppSelector((state) =>
        selectOmniTicketConfiguredMessageTypes(state, queueKey),
    );

    const { data: templates, isLoading: templatesLoading } =
        useAutoReplyTemplates(queueKey);

    if (profiles.isEmpty() || templatesLoading) {
        return <Loading />;
    }

    if (configuredMessageTypes !== null && configuredMessageTypes.size === 0) {
        return (
            <Redirect
                to={generatePath(routes['pbx.queues.entityKey.settings'], {
                    entityKey,
                })}
            />
        );
    }

    return (
        <Page
            header={i18next.t('Queue.Settings.List.WelcomeMessage')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['pbx.queues.entityKey.settings'], {
                        entityKey,
                    }),
                )
            }
        >
            <div className="flex items-center">
                {i18next.t('tickets.autoreply.info')}
            </div>

            {profiles
                .map((profile) => {
                    const profileKey = profile.get('key');

                    return (
                        <div key={profileKey}>
                            <Divider />
                            <SettingSection>
                                {profile.get('description')}
                            </SettingSection>
                            {AUTO_REPLY_MESSAGE_TYPES.filter(
                                (type) =>
                                    configuredMessageTypes === null ||
                                    configuredMessageTypes.has(type),
                            ).map((messageType) => {
                                const existing = templates?.find(
                                    (t) =>
                                        t.profileEntityKey === profileKey &&
                                        t.messageType === messageType,
                                );

                                return (
                                    <RouteLink
                                        key={messageType}
                                        to={generatePath(
                                            routes[
                                                'pbx.queues.entityKey.settings.autoreply.profileKey.messageType'
                                            ],
                                            {
                                                entityKey,
                                                profileKey,
                                                messageType,
                                            },
                                        )}
                                        description={
                                            existing ? (
                                                <span className="line-clamp-1">
                                                    {existing.messageTemplate}
                                                </span>
                                            ) : undefined
                                        }
                                    >
                                        <span className="whitespace-nowrap">
                                            {i18next.t(
                                                `tickets.autoreply.messageType.${messageType}`,
                                            )}
                                        </span>
                                    </RouteLink>
                                );
                            })}
                        </div>
                    );
                })
                .toArray()}
        </Page>
    );
};

export default PbxAutoReply;
